import { useCallback, useRef, useState } from "react";
import Page from "../../layout/Page";
import Herobox from "../../components/Herobox";
import ContentSection from "../../components/ContentSection";
import NotFoundPage from "../NotFoundPage";
import "./style.scss";
import ImageOrVideo from "../../components/ImageOrVideo";
import BjelinButton from "../../components/BjelinButton";
import { unwrapAsset } from "../../utils/CockpitUtils";
import Breadcrumbs from "../../components/Breadcrumbs";
import { useClaimType } from "../../hooks/ClaimsHooks";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { transformHtml } from "../../utils/CmsUtils";
import {
  claimsLandingUrl,
  claimsTypeFormPageUrl,
  claimsTypesListUrl,
  claimsWholesaleFormUrl
} from "../../config/urls";
import CockpitEditLink from "../../components/CockpitEditLink";
import { useSearchParams } from "react-router-dom";

const ClaimTypePage = ({ claimTypeId }) => {
  const claimType = useClaimType(claimTypeId);
  const [params] = useSearchParams();
  const wholesale = params.get("wholesale") !== null;
  const t = useBjelinTranslations();

  const [allChecked, setAllChecked] = useState(false);
  const checkListRefs = useRef([]);
  const validateReqs = useCallback(() => {
    setAllChecked(!checkListRefs.current.find(ref => !ref.checked));
  }, []);

  if (!claimType) {
    return <NotFoundPage />;
  }

  const title = claimType.title;
  const breadcrumbItems = [
    { url: claimsLandingUrl, text: t("claims.heading") },
    {
      url: claimsTypesListUrl(wholesale),
      text: t("claims.reason.choose")
    }
  ];

  return (
    <>
      <Page
        name="claim-type-page"
        key={"claim-type-page" + title}
        title={title}
      >
        <Herobox
          type="slim"
          textShadow={true}
          className={"text-center"}
          centerImage={true}
        >
          <h1>{title}</h1>
        </Herobox>

        <ContentSection>
          <Breadcrumbs items={breadcrumbItems} />
          <h1>{title}</h1>
          <div className="claim-types-descr">
            {transformHtml(claimType.description)}
          </div>
        </ContentSection>
        {claimType.requirements.length > 0 ? (
          <>
            <ContentSection>
              <h2>{t("claims.heading.checklist")}</h2>
              <p style={{ marginBottom: "2rem" }}>
                {t("claims.descr.checklist")}
              </p>
              <div className="reqs">
                {claimType.requirements
                  .map(r => r.value)
                  .map((req, index) => (
                    <label
                      htmlFor={"check" + index}
                      className="req"
                      key={index}
                    >
                      <div className="req-checkbox">
                        <input
                          id={"check" + index}
                          ref={ref => (checkListRefs.current[index] = ref)}
                          type="checkbox"
                          onChange={validateReqs}
                          style={{ height: "2em", width: "2em" }}
                        />
                      </div>
                      <div className="req-content">
                        <h4>{req.heading}</h4>
                        <div className="req-descr">
                          {transformHtml(req.description)}
                        </div>
                      </div>
                      {req.image && (
                        <div className="req-image">
                          <ImageOrVideo
                            image={unwrapAsset(req.image)}
                            className="req-image"
                            centerImage
                          />
                        </div>
                      )}
                    </label>
                  ))}
              </div>
            </ContentSection>
            <ContentSection padded>
              <BjelinButton url={claimsTypesListUrl(wholesale)} color="white">
                {t("back")}
              </BjelinButton>{" "}
              <BjelinButton
                url={
                  wholesale
                    ? claimsWholesaleFormUrl(claimType.id)
                    : claimsTypeFormPageUrl(claimType)
                }
                color="primary"
                disabled={!allChecked}
              >
                {t("continue")}
              </BjelinButton>
              {!allChecked && (
                <p style={{ marginTop: "1rem", fontSize: "0.8em" }}>
                  {t("claims.descr.all-checkboxes")}
                </p>
              )}
            </ContentSection>
          </>
        ) : (
          <ContentSection padded>
            <BjelinButton url={claimsTypesListUrl(wholesale)} color="white">
              {t("back")}
            </BjelinButton>{" "}
            <BjelinButton
              url={
                wholesale
                  ? claimsWholesaleFormUrl(claimType.id)
                  : claimsTypeFormPageUrl(claimType)
              }
              color="primary"
            >
              {t("continue")}
            </BjelinButton>
          </ContentSection>
        )}
        <CockpitEditLink
          collection={"claim_types"}
          _id={claimType._id}
          labelKey={"action.edit"}
          wrapper={children => (
            <ContentSection containerTheme={"white"} className={"text-center"}>
              {children}
            </ContentSection>
          )}
        />
      </Page>
    </>
  );
};

export default ClaimTypePage;
